import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysDeliveryNote } from '../queryKeys';

import { deleteDeliveryNotesAdmin } from './deleteDeliveryNotesAdmin';
import {
  type DeleteDeliveryNotesAdminMutationOptions,
  type DeleteDeliveryNotesAdminVariables,
} from './types';

/**
 * Permanently deletes delivery notes of any company account.
 *
 * On success the cached details of the notes are dropped and every other delivery note query is
 * marked stale, so lists and counts refetch the next time they are shown. Nothing refetches
 * immediately: an observed query of a deleted note would only answer 404.
 *
 * @param options - Additional mutation options; callbacks run after the hook's own.
 * @returns The delete-delivery-notes mutation.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/delete_dlns_route_asset_delivery_note_admin_delete_dlns_delete
 */
export function useMutationDeleteDeliveryNotesAdmin(
  options?: DeleteDeliveryNotesAdminMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({
      deliveryNoteIds,
    }: DeleteDeliveryNotesAdminVariables) =>
      deleteDeliveryNotesAdmin(deliveryNoteIds),
    async onSuccess(data, variables, onMutateResult, context) {
      for (const deliveryNoteId of variables.deliveryNoteIds) {
        queryClient.removeQueries({
          exact: true,
          queryKey: queryKeysDeliveryNote.get(deliveryNoteId),
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeysDeliveryNote.base()],
          refetchType: 'none',
        }),
        options?.onSuccess?.(data, variables, onMutateResult, context),
      ]);
    },
  });
}
